import { useState, useEffect, useRef } from 'react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import userIcon from '@iconify/icons-fa-solid/user';
import coinsIcon from '@iconify/icons-fa-solid/coins';
import trophyIcon from '@iconify/icons-fa-solid/trophy';
import chartLine from '@iconify/icons-fa-solid/chart-line';
import { TextGold } from '@/components/common';
import styles from './StatsCard.module.css';

// Simulate real-time fluctuation around a base value
const useFluctuatingValue = (base, range, intervalMs = 3000) => {
  const [value, setValue] = useState(base);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const tick = () => {
      const delta = (Math.random() - 0.4) * range; // slight upward bias
      setValue(prev => {
        const next = prev + delta;
        // Keep within reasonable bounds
        return Math.max(base * 0.85, Math.min(base * 1.2, next));
      });
      // Random interval for natural feel
      timeoutRef.current = setTimeout(tick, intervalMs + Math.random() * 2000);
    };
    timeoutRef.current = setTimeout(tick, intervalMs);
    return () => clearTimeout(timeoutRef.current);
  }, [base, range, intervalMs]);

  return value;
};

const formatK = (n) => {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return Math.round(n).toString();
};

const formatM = (n) => {
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(0)}K`;
  return `$${Math.round(n)}`;
};

const StatsCard = () => {
  const { t } = useTranslation();

  const onlineUsers = useFluctuatingValue(14200, 300, 2500);
  const paidToday = useFluctuatingValue(2300000, 50000, 4000);
  const p2pOffers = useFluctuatingValue(48, 3, 5000);

  const STATS = [
    { value: formatK(onlineUsers), labelKey: 'home.onlineNow', icon: userIcon },
    { value: formatM(paidToday), labelKey: 'home.paidToday', icon: coinsIcon },
    { value: '5 Tiers', labelKey: 'vip.vipClub', icon: trophyIcon },
    { value: `${Math.round(p2pOffers)}+`, labelKey: 'home.p2pOffers', icon: chartLine },
  ];

  return (
    <div className={styles.bottomRow}>
      <div className={styles.headlineCard}>
        <TextGold
          as="h2"
          size="clamp(0.8rem, 1.5vw, 1rem)"
          className={styles.headlineText}
        >
          {t('home.tradePlayEarn')}
        </TextGold>
        <p className={styles.headlineSub}>
          {t('home.p2pExchange')} &bull; {t('common.casino')} &bull; {t('common.sports')} &bull; {t('home.vipRewards')}
        </p>
      </div>

      <div className={styles.statsRow}>
        {STATS.map(({ value, labelKey, icon }) => (
          <div key={labelKey} className={styles.statItem}>
            <Icon icon={icon} className={styles.statIcon} />
            <span className={styles.statValue}>{value}</span>
            <span className={styles.statLabel}>{t(labelKey)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StatsCard;
